/**
 * Weather hook + imperative fetch for Open-Meteo.
 * No API key needed. Client-side CORS supported.
 *
 * Two exports:
 * - useWeather(coords)     — React hook for UI (Filters widget)
 * - fetchWeather(lat, lng) — imperative for handleSubmit pipeline
 *
 * Both share a 30-minute module-level cache keyed by rounded coords.
 */

import { useState, useEffect, useRef } from 'react';
import { describeWeatherCode } from '@/utils/weatherCodes';

export interface WeatherData {
  temperature: number;        // °C
  apparentTemperature: number; // °C (feels like)
  weatherCode: number;
  windSpeed: number;           // km/h
  precipitation: number;       // mm
  label: string;
  emoji: string;
  prefersIndoor: boolean;
  fetchedAt: number;           // Date.now()
}

// ─── Cache ────────────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface CacheEntry {
  data: WeatherData;
  fetchedAt: number;
}

const cache = new Map<string, CacheEntry>();

function cacheKey(lat: number, lng: number): string {
  // ~1 km precision — good enough for weather
  return `${lat.toFixed(2)},${lng.toFixed(2)}`;
}

function getCached(lat: number, lng: number): WeatherData | null {
  const key = cacheKey(lat, lng);
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

const OPEN_METEO_URL = 'https://api.open-meteo.com/v1/forecast';

export async function fetchWeather(lat: number, lng: number): Promise<WeatherData | null> {
  // Check cache first
  const cached = getCached(lat, lng);
  if (cached) return cached;

  try {
    const url = `${OPEN_METEO_URL}?latitude=${lat.toFixed(4)}&longitude=${lng.toFixed(4)}&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,precipitation&timezone=auto`;
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[Weather] Open-Meteo returned ${res.status}`);
      return null;
    }

    const json = await res.json();
    const current = json.current;
    if (!current) return null;

    const description = describeWeatherCode(current.weather_code ?? 0);

    const data: WeatherData = {
      temperature: Math.round(current.temperature_2m ?? 0),
      apparentTemperature: Math.round(current.apparent_temperature ?? 0),
      weatherCode: current.weather_code ?? 0,
      windSpeed: Math.round(current.wind_speed_10m ?? 0),
      precipitation: current.precipitation ?? 0,
      label: description.label,
      emoji: description.emoji,
      prefersIndoor: description.prefersIndoor,
      fetchedAt: Date.now(),
    };

    cache.set(cacheKey(lat, lng), { data, fetchedAt: Date.now() });
    return data;
  } catch (err) {
    console.warn('[Weather] Failed to fetch:', err);
    return null;
  }
}

// ─── React hook ───────────────────────────────────────────────────────────────

export function useWeather(coords: { lat: number; lng: number } | null | undefined) {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (!coords) {
      setWeather(null);
      lastKey.current = null;
      return;
    }

    const key = cacheKey(coords.lat, coords.lng);
    if (key === lastKey.current) return; // Same location, skip
    lastKey.current = key;

    // Check cache synchronously
    const cached = getCached(coords.lat, coords.lng);
    if (cached) {
      setWeather(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchWeather(coords.lat, coords.lng).then(data => {
      if (cancelled) return;
      setWeather(data);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [coords?.lat, coords?.lng]);

  return { weather, loading };
}
